import {
	DARK_THEME_COLOR_DEFAULTS,
	LIGHT_THEME_COLOR_DEFAULTS,
	THEME_COLOR_TOKEN_KEYS,
	type ThemeColorTokenKey,
	themeColorCssVar,
	themeColorCssVarAliasDeclarations,
} from "./themeColorTokens";
import {
	BUILDER_BUTTON_LINK_CLASS,
	buildTypographyStylesByViewport,
	isLegacyDefaultSerifFontFamily,
	isSiteSansFontFamilyToken,
	resolveSiteSansFontFamilyForCss,
	resolveTypographyFontFamilyForCss,
	TYPOGRAPHY_BREAKPOINT_ORDER,
	TYPOGRAPHY_ELEMENT_ORDER,
	type TypographyBreakpointKey,
	type TypographyElementStyle,
	typographyElementSelector,
} from "./typographyTokens";
import {
	normalizeThemeColorMode,
	type ThemeColorMode,
} from "./visitorThemeModes";

/** Builder canvas + in-app preview scope (matches region `--tt-color-*` tokens). */
export const BUILDER_THEME_SCOPE_CLASS = "pb-builder-theme-scope";

/**
 * DOM attribute marking which kit the builder canvas is currently painting
 * — set on an ancestor of `.pb-builder-theme-scope` (see `BuilderCanvasModeContext.tsx`).
 * Deliberately NOT the bare `dark` class: the admin dashboard shell
 * (`apps/tenant-dashboard/src/components/providers.tsx`, next-themes
 * `attribute="class"`) also puts a bare `dark` class on `<html>` when the
 * admin UI (not the canvas) is in dark mode. A selector of `.dark
 * .pb-builder-theme-scope` matches that ancestor too, so builder canvas
 * Theme colors used to silently resolve to the dark kit whenever the admin
 * shell happened to be dark — regardless of the canvas's own Light|Dark
 * toggle. This attribute can never collide with next-themes' class-based
 * scheme.
 */
export const BUILDER_CANVAS_DARK_MODE_ATTR = "data-builder-canvas-mode";

/** `[data-builder-canvas-mode="dark"]` — the dark half of the builder-only theme CSS below. */
export const BUILDER_CANVAS_DARK_SELECTOR = `[${BUILDER_CANVAS_DARK_MODE_ATTR}="dark"]`;

/**
 * Public content root for Theme Manager *tag* typography (`a`, `h1`–`h6`, …).
 * Region layout keeps body `font-family` / `font-size` global with `!important`,
 * but scopes tag rules here so mega menu / global chrome outside this attr are
 * not crushed. Post-type typography overrides reuse the same selector.
 */
export const ENTRY_TYPOGRAPHY_SCOPE_SELECTOR = "[data-entry-typography]";

/**
 * Opt-in marker. When a page sets `settings.themeFontsInCustomCode`, the entry
 * (and builder canvas) root carries this attribute so custom-code headings can
 * use Theme Manager font families without restyling every existing block.
 */
export const CUSTOM_CODE_THEME_FONTS_ATTR = "data-pb-theme-fonts";

/**
 * Region wrapper around Theme Manager global elements (header / footer / banners)
 * via `PublicEntryShell`. Font-family only — never full tag `a` rules.
 */
export const GLOBAL_SLOT_SCOPE_SELECTOR = "[data-global-slot]";

/**
 * CamelCase CSS props that Theme Manager tag rules emit with `!important`.
 * Explicit ElementStyles for these keys must also use `!important` so they win.
 */
export const THEME_TYPOGRAPHY_IMPORTANT_PROPS = [
	"fontFamily",
	"fontSize",
	"fontWeight",
	"lineHeight",
	"letterSpacing",
	"color",
	"fontStyle",
	"textDecoration",
	"textTransform",
	"textAlign",
	/** Text/Heading “Line spacing” (Above/Below) — theme defaults + element overrides. */
	"paddingTop",
	"paddingBottom",
	"backgroundImage",
	"backgroundClip",
	"WebkitBackgroundClip",
	"WebkitTextFillColor",
] as const;

export type ThemeTypographyImportantProp =
	(typeof THEME_TYPOGRAPHY_IMPORTANT_PROPS)[number];

/** Strip a trailing `!important` so the value is valid for React inline styles. */
export function stripCssImportant(value: string): string {
	return value.replace(/\s*!important\s*$/i, "").trim();
}

/**
 * CSS cascade keywords. Storing these on ElementStyles and emitting them with
 * `!important` (beat-theme rules) overrides Theme Manager tag styles — treat as
 * "clear override" instead.
 */
export function isCssCascadeKeyword(value: string): boolean {
	return /^(inherit|initial|unset|revert|revert-layer)$/i.test(value.trim());
}

/**
 * @deprecated Prefer {@link stripCssImportant}. React cannot apply `!important`
 * via the `style` prop (browsers reject `"36px !important"` as a value).
 * Kept as an alias that strips rather than appends.
 */
export function importantCssValue(value: string): string {
	return stripCssImportant(value);
}

/**
 * CSS scalar from a flat ElementStyles value.
 * Strings and finite numbers pass through. Token refs `{ value }` / `{ token }`
 * and breakpoint bags `{ desktop, tablet, mobile }` unwrap to a string so
 * React inline styles and beat-theme CSS do not drop them.
 */
export function readStyleScalar(raw: unknown): string {
	if (typeof raw === "string") {
		return raw;
	}
	if (typeof raw === "number" && Number.isFinite(raw)) {
		return String(raw);
	}
	if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
		return "";
	}
	const record = raw as Record<string, unknown>;
	if ("value" in record) {
		const nested = readStyleScalar(record.value).trim();
		if (nested) {
			return nested;
		}
	}
	if (typeof record.token === "string" && record.token.trim()) {
		return record.token.trim();
	}
	if ("desktop" in record || "tablet" in record || "mobile" in record) {
		return (
			readStyleScalar(record.desktop).trim() ||
			readStyleScalar(record.tablet).trim() ||
			readStyleScalar(record.mobile).trim()
		);
	}
	return "";
}

/**
 * Sanitize typography values for React `style={{…}}`.
 *
 * React/DOM assignment cannot set `!important` on inline styles; appending
 * `" !important"` to the value makes the declaration invalid and drops it.
 * Theme-beating `!important` is emitted as a real stylesheet via
 * `collectElementTypographyOverridesCss` / `responsiveOverridesCss`.
 */
export function beatThemeTypographyImportant(
	css: Record<string, unknown>
): Record<string, unknown> {
	const out: Record<string, unknown> = { ...css };
	for (const key of THEME_TYPOGRAPHY_IMPORTANT_PROPS) {
		const raw = out[key];
		if (typeof raw === "string" && raw.trim().length > 0) {
			out[key] = stripCssImportant(raw);
		} else if (typeof raw === "number" && key === "fontWeight") {
			out[key] = String(raw);
		} else if (typeof raw === "object" && raw !== null) {
			const scalar = readStyleScalar(raw).trim();
			if (scalar) {
				out[key] = stripCssImportant(scalar);
			}
		}
	}
	return out;
}

export type BuilderThemeColorTokens = Record<ThemeColorTokenKey, string | null>;

export type BuilderThemeColors = {
	light: BuilderThemeColorTokens;
	dark: BuilderThemeColorTokens;
	/**
	 * Sibling fields on the same Theme Manager `colors` JSON, not per-token
	 * color data — feed `resolveVisitorThemeModePolicy` (`visitorThemeModes.ts`)
	 * to gate the builder's Light|Dark controls by what visitors can actually
	 * reach on the public site.
	 */
	defaultMode: ThemeColorMode;
	allowVisitorThemeSwitcher: boolean;
};

export type BuilderThemeTypography = {
	fontSans: string | null;
	fontSerif: string | null;
	fontMono: string | null;
	baseFontSize: string | null;
	elements: Record<string, unknown>;
};

const VIEWPORT_QUERY: Record<TypographyBreakpointKey, string | null> = {
	desktop: null,
	tablet: "@media (max-width: 1024px)",
	mobile: "@media (max-width: 640px)",
};

const STYLE_KEYS = [
	["fontFamily", "font-family"],
	["fontSize", "font-size"],
	["fontWeight", "font-weight"],
	["lineHeight", "line-height"],
	["letterSpacing", "letter-spacing"],
	["color", "color"],
	["fontStyle", "font-style"],
	["textDecoration", "text-decoration"],
	["textTransform", "text-transform"],
	["textAlign", "text-align"],
	["paddingTop", "padding-top"],
	["paddingBottom", "padding-bottom"],
	["backgroundImage", "background-image"],
	["backgroundClip", "background-clip"],
	["WebkitBackgroundClip", "-webkit-background-clip"],
	["WebkitTextFillColor", "-webkit-text-fill-color"],
] as const;

type StyleKey = Exclude<keyof TypographyElementStyle, "colorDark">;

function asObject(value: unknown): Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: {};
}

function asNonEmptyString(value: unknown): string | null {
	return typeof value === "string" && value.trim().length > 0
		? value.trim()
		: null;
}

function tokensFrom(raw: unknown): BuilderThemeColorTokens {
	const o = asObject(raw);
	const out = {} as BuilderThemeColorTokens;
	for (const key of THEME_COLOR_TOKEN_KEYS) {
		out[key] = asNonEmptyString(o[key]);
	}
	return out;
}

/** Normalize Theme Manager `colors` JSON (legacy flat or light/dark buckets). */
export function normalizeBuilderThemeColors(raw: unknown): BuilderThemeColors {
	const o = asObject(raw);
	const legacy = tokensFrom(o);
	const lightExplicit = tokensFrom(o.light);
	const darkExplicit = tokensFrom(o.dark);
	const lightHasExplicit = Object.values(lightExplicit).some((v) => v !== null);
	const light = lightHasExplicit ? lightExplicit : legacy;
	return {
		light,
		dark: darkExplicit,
		defaultMode: normalizeThemeColorMode(o.defaultMode),
		allowVisitorThemeSwitcher: o.allowVisitorThemeSwitcher === true,
	};
}

export function normalizeBuilderThemeTypography(
	raw: unknown
): BuilderThemeTypography {
	const o = asObject(raw);
	return {
		fontSans: asNonEmptyString(o.fontSans),
		fontSerif: asNonEmptyString(o.fontSerif),
		fontMono: asNonEmptyString(o.fontMono),
		baseFontSize: asNonEmptyString(o.baseFontSize),
		elements: asObject(o.elements),
	};
}

function tokensToVarBlock(
	selector: string,
	tokens: BuilderThemeColorTokens,
	defaults: Record<ThemeColorTokenKey, string>
): string {
	const lines: string[] = [];
	for (const key of THEME_COLOR_TOKEN_KEYS) {
		const value = tokens[key] || defaults[key];
		lines.push(`${themeColorCssVar(key)}: ${value};`);
	}
	lines.push(...themeColorCssVarAliasDeclarations());
	return `${selector} { ${lines.join(" ")} }`;
}

/**
 * Cascade contract (keep in sync with ELEMENT_SETTINGS_SPEC §4.5):
 * 1. Theme Manager tag rules use `!important` (strict site defaults).
 * 2. Tag rules are scoped to content (`ENTRY_TYPOGRAPHY_SCOPE_SELECTOR` on
 *    Region) and exclude mega / replica chrome (`MEGA_MENU_TYPOGRAPHY_EXCLUDE`:
 *    `[data-pb-mega-menu]`, `[data-pb-advanced-mega-menu]`, `[data-ghh-flyout]`).
 * 3. Global chrome (`GLOBAL_SLOT_SCOPE_SELECTOR`) gets site Sans `font-family`
 *    only via {@link buildGlobalChromeFontFamilyCss} — never link color /
 *    underline from Theme Manager `a` rules (mega menu keeps its own colors).
 * 4. Explicit ElementStyles for the same props are emitted as a real
 *    stylesheet (`collectElementTypographyOverridesCss`) with `!important`
 *    and `html body` + `[data-pb-node]` + {@link TYPOGRAPHY_SCOPED_EXCLUDES}
 *    so specificity beats scoped tag rules (e.g. `[data-entry-typography] h2`).
 *    React inline styles stay without `!important` (browsers reject via style props).
 * Custom properties (`--tt-*`) are assigned without `!important`.
 */

/**
 * Strong Theme Manager Sans on Region global slots (footer / header / banners)
 * and plain `navigation_menu` roots — without applying entry tag `a` color or
 * text-decoration. Descendants use `!important` so builder inline / module CSS
 * cannot fall back to UA Times.
 *
 * Uses the resolved Sans stack literally (not only `var(--tt-font-sans)`) so a
 * missing/invalid custom property cannot invalidate the whole declaration.
 * Mega-menu colors stay untouched (this rule sets `font-family` only).
 */
export function buildGlobalChromeFontFamilyCss(
	fontSans: string | null | undefined,
	savedElements?: unknown
): string {
	const family = resolveSiteSansFontFamilyForCss(fontSans, savedElements);
	const slot = GLOBAL_SLOT_SCOPE_SELECTOR;
	// `html body` + attr raises specificity over single-class / inline (non-!important).
	return (
		[
			`html body ${slot}, html body ${slot} *`,
			"html body [data-pb-nav-menu], html body [data-pb-nav-menu] *",
		].join(", ") + ` { font-family: ${family} !important; }`
	);
}

/**
 * Pin site Sans on public content roots (archives / author / glossary / entries)
 * so auto-generated pages inherit Theme Sans instead of UA Times.
 *
 * Pin the **root only** — not `*`. A descendant `*` pin with `html body` beats
 * Theme Manager tag `font-family` (e.g. Manrope on `p`) even when both use
 * `!important`. Per-tag and per-element fonts must win on the tags themselves;
 * other descendants inherit from this root.
 */
export function buildEntryTypographyFontFamilyCss(
	fontSans: string | null | undefined,
	savedElements?: unknown
): string {
	const family = resolveSiteSansFontFamilyForCss(fontSans, savedElements);
	const root = ENTRY_TYPOGRAPHY_SCOPE_SELECTOR;
	return `html body ${root} { font-family: ${family} !important; }`;
}

function declarationsFor(
	style: TypographyElementStyle,
	fontSans: string | null | undefined,
	savedElements?: unknown
): string {
	const out: string[] = [];
	for (const [key, css] of STYLE_KEYS) {
		const v = style[key as StyleKey];
		if (typeof v === "string" && v.trim().length > 0) {
			let value = v.trim();
			if (key === "fontFamily") {
				const resolved = resolveTypographyFontFamilyForCss(value);
				// Prefer the literal Theme Sans stack when the tag asks for site Sans
				// so a missing `--tt-font-sans` cannot invalidate `!important`.
				value = isSiteSansFontFamilyToken(resolved)
					? resolveSiteSansFontFamilyForCss(fontSans, savedElements)
					: resolved;
			}
			out.push(`${css}: ${value} !important;`);
		}
	}
	return out.join(" ");
}

function withScope(selector: string, scopeSelector: string): string {
	return selector
		.split(",")
		.map((part) => `${scopeSelector} ${part.trim()}`)
		.join(", ");
}

function withDarkScope(
	selector: string,
	scopeSelector: string,
	darkModeSelector: string
): string {
	return selector
		.split(",")
		.map((part) => `${darkModeSelector} ${scopeSelector} ${part.trim()}`)
		.join(", ");
}

/** Builder-only — the dark half is always scoped by `BUILDER_CANVAS_DARK_SELECTOR`, never bare `.dark` (see its doc comment). Public/Region color CSS is built independently in `apps/tenant-region-instance/src/components/tenant-theme-style.tsx` and is unaffected. */
function buildScopedColorVariables(
	colors: BuilderThemeColors,
	scopeSelector: string
): string {
	return [
		tokensToVarBlock(scopeSelector, colors.light, LIGHT_THEME_COLOR_DEFAULTS),
		tokensToVarBlock(
			`${BUILDER_CANVAS_DARK_SELECTOR} ${scopeSelector}`,
			colors.dark,
			DARK_THEME_COLOR_DEFAULTS
		),
	].join(" ");
}

function buildScopedTypographyVars(
	typography: BuilderThemeTypography,
	scopeSelector: string
): string {
	const lines: string[] = [];
	const siteSans = resolveSiteSansFontFamilyForCss(
		typography.fontSans,
		typography.elements
	);
	// Always assign a concrete stack — never `var(--tt-font-sans, …)`.
	lines.push(`--tt-font-sans: ${siteSans};`);
	lines.push(`--font-sans: ${siteSans};`);
	lines.push(`font-family: ${siteSans} !important;`);
	if (
		typography.fontSerif &&
		!isLegacyDefaultSerifFontFamily(typography.fontSerif)
	) {
		lines.push(`--tt-font-serif: ${typography.fontSerif};`);
	}
	if (typography.fontMono) {
		lines.push(`--tt-font-mono: ${typography.fontMono};`);
	}
	if (typography.baseFontSize) {
		lines.push(`--tt-base-font-size: ${typography.baseFontSize};`);
		lines.push("font-size: var(--tt-base-font-size) !important;");
	}
	return `${scopeSelector} { ${lines.join(" ")} }`;
}

const TAG_VAR_PROPS = [
	["fontFamily", "font-family"],
	["fontSize", "font-size"],
	["fontWeight", "font-weight"],
	["lineHeight", "line-height"],
	["letterSpacing", "letter-spacing"],
] as const;

function tagVarDeclaration(
	tag: string,
	prop: (typeof TAG_VAR_PROPS)[number][0],
	cssProp: string,
	raw: string
): string | null {
	let value = raw
		.trim()
		.replace(/\s*!important\s*$/i, "")
		.trim();
	if (!value) {
		return null;
	}
	if (prop === "fontFamily") {
		value = resolveTypographyFontFamilyForCss(value);
	}
	// A custom property must not refer to itself (or any --tt-type-* var).
	if (!value || value.includes("--tt-type-")) {
		return null;
	}
	return `--tt-type-${tag}-${cssProp}: ${value};`;
}

/**
 * Per-tag custom properties (`--tt-type-{tag}-{prop}`) for font-family,
 * font-size, font-weight, line-height, and letter-spacing. Emitted per
 * breakpoint on `selector` (public `:root`, a typography-set scope, or the
 * builder canvas). No `!important` — these are custom properties only.
 */
export function buildTypographyTagVarsCss(
	typography: BuilderThemeTypography,
	selector: string
): string {
	const byVp = buildTypographyStylesByViewport(typography.elements);
	const sections: string[] = [];
	for (const vp of TYPOGRAPHY_BREAKPOINT_ORDER) {
		const decls: string[] = [];
		for (const key of TYPOGRAPHY_ELEMENT_ORDER) {
			const style = byVp[vp][key];
			for (const [prop, cssProp] of TAG_VAR_PROPS) {
				const raw = style[prop];
				if (typeof raw !== "string" || raw.trim().length === 0) {
					continue;
				}
				const decl = tagVarDeclaration(key, prop, cssProp, raw);
				if (decl) {
					decls.push(decl);
				}
			}
		}
		if (decls.length === 0) {
			continue;
		}
		const block = `${selector} { ${decls.join(" ")} }`;
		const wrapper = VIEWPORT_QUERY[vp];
		sections.push(wrapper ? `${wrapper} { ${block} }` : block);
	}
	return sections.join(" ");
}

const CUSTOM_CODE_THEME_FONT_TAGS: readonly {
	key: string;
	selectors: readonly string[];
}[] = [
	{ key: "h1", selectors: ["h1"] },
	{ key: "h2", selectors: ["h2"] },
	{ key: "h3", selectors: ["h3"] },
	{ key: "h4", selectors: ["h4"] },
	{ key: "h5", selectors: ["h5"] },
	{ key: "h6", selectors: ["h6"] },
	{ key: "p", selectors: ["p"] },
	{ key: "blockquote", selectors: ["blockquote"] },
	{ key: "a", selectors: ["a"] },
	{ key: "ul", selectors: ["ul", "ol"] },
	{ key: "pre", selectors: ["pre"] },
];

/**
 * Font-family only, and only inside custom code on pages that opt in via
 * {@link CUSTOM_CODE_THEME_FONTS_ATTR}. Sizes, colours, and spacing stay in
 * the block's own CSS. `enabled: false` emits nothing.
 */
export function buildCustomCodeThemeFontsCss(options?: {
	enabled?: boolean;
	scopeSelector?: string;
}): string {
	if (options?.enabled === false) {
		return "";
	}
	const host = options?.scopeSelector
		? `${options.scopeSelector}[${CUSTOM_CODE_THEME_FONTS_ATTR}]`
		: `[${CUSTOM_CODE_THEME_FONTS_ATTR}]`;
	return CUSTOM_CODE_THEME_FONT_TAGS.map(({ key, selectors }) => {
		const selector = selectors
			.map(
				(tag) =>
					`${host} [data-pb-custom-code-root] ${tag}:not(:where([data-pb-own-type] *))`
			)
			.join(", ");
		return `${selector} { font-family: var(--tt-type-${key}-font-family) !important; }`;
	}).join(" ");
}

/** True only when the page explicitly opted in. Absent / false stays off. */
export function themeFontsInCustomCodeFromSettings(settings: unknown): boolean {
	if (
		typeof settings !== "object" ||
		settings === null ||
		Array.isArray(settings)
	) {
		return false;
	}
	return (
		(settings as { themeFontsInCustomCode?: unknown })
			.themeFontsInCustomCode === true
	);
}

/**
 * Per-element typography rules for builder canvas or public site
 * (`scopeSelector` optional). Shared by both — `apps/tenant-region-instance`
 * calls this directly for the public site and relies on the `darkModeSelector`
 * default (`.dark`, the visitor document's real dark-mode class); the builder
 * canvas passes `BUILDER_CANVAS_DARK_SELECTOR` instead (see `buildScopedTypographyRules`)
 * so its own dark-kit typography (`colorDark`) can't be triggered by the
 * admin dashboard shell's unrelated `html.dark`.
 */
/** Strip Theme Manager / UA link underline from native builder button `<a>`s. */
export function buildBuilderButtonLinkResetCss(scopeSelector?: string): string {
	const sel = scopeSelector
		? withScope(`a.${BUILDER_BUTTON_LINK_CLASS}`, scopeSelector)
		: `a.${BUILDER_BUTTON_LINK_CLASS}`;
	return `${sel} { text-decoration: none !important; }`;
}

export function buildTypographyRulesCss(
	typography: BuilderThemeTypography,
	scopeSelector?: string,
	darkModeSelector = ".dark"
): string {
	const buttonReset = buildBuilderButtonLinkResetCss(scopeSelector);
	if (Object.keys(typography.elements).length === 0) {
		return buttonReset;
	}
	const byVp = buildTypographyStylesByViewport(typography.elements);
	const sections: string[] = [];
	for (const vp of TYPOGRAPHY_BREAKPOINT_ORDER) {
		const lightRules: string[] = [];
		const darkRules: string[] = [];
		for (const key of TYPOGRAPHY_ELEMENT_ORDER) {
			const style = byVp[vp][key];
			const decl = declarationsFor(
				style,
				typography.fontSans,
				typography.elements
			);
			const selector = typographyElementSelector(key);
			const scopedSelector = scopeSelector
				? withScope(selector, scopeSelector)
				: selector;
			if (decl) {
				lightRules.push(`${scopedSelector} { ${decl} }`);
			}
			const darkColor = style.colorDark;
			if (typeof darkColor === "string" && darkColor.trim().length > 0) {
				const darkSelector = scopeSelector
					? withDarkScope(selector, scopeSelector, darkModeSelector)
					: `${darkModeSelector} ${selector}`;
				darkRules.push(
					`${darkSelector} { color: ${darkColor.trim()} !important; }`
				);
			}
		}
		const combined = [...lightRules, ...darkRules];
		if (combined.length === 0) {
			continue;
		}
		const wrapper = VIEWPORT_QUERY[vp];
		sections.push(
			wrapper ? `${wrapper} { ${combined.join(" ")} }` : combined.join(" ")
		);
	}
	return [sections.join(" "), buttonReset].filter(Boolean).join(" ");
}

function buildScopedTypographyRules(
	typography: BuilderThemeTypography,
	scopeSelector: string
): string {
	return buildTypographyRulesCss(
		typography,
		scopeSelector,
		BUILDER_CANVAS_DARK_SELECTOR
	);
}

export function escapeThemeCssForStyleTag(value: string): string {
	return value.replace(/<\/style/gi, "<\\/style");
}

/** Scoped tenant theme CSS for builder canvas / in-app preview (WYSIWYG with region). */
export function buildBuilderCanvasThemeCss(options: {
	colors: BuilderThemeColors;
	typography?: BuilderThemeTypography;
	scopeSelector?: string;
}): string {
	const scopeSelector =
		options.scopeSelector ?? `.${BUILDER_THEME_SCOPE_CLASS}`;
	const typography = options.typography ?? {
		fontSans: null,
		fontSerif: null,
		fontMono: null,
		baseFontSize: null,
		elements: {},
	};
	return [
		buildScopedColorVariables(options.colors, scopeSelector),
		buildScopedTypographyVars(typography, scopeSelector),
		buildTypographyTagVarsCss(typography, scopeSelector),
		buildScopedTypographyRules(typography, scopeSelector),
		buildCustomCodeThemeFontsCss({ scopeSelector }),
	]
		.filter(Boolean)
		.join(" ");
}
